/**
 * TeamWorkloadBar — one developer's open work as a thin stacked bar, plus the shared legend.
 *
 * ===========================================================================
 * THE FORM, AND WHY IT IS HTML RATHER THAN AN <svg>
 * ===========================================================================
 * Per developer the data's job is part-to-whole over an ORDERED set of stages, which
 * is a stacked horizontal bar (dataviz skill, `choosing-a-form.md`). The mark specs
 * are fixed: ≤ 24px thick (this is 12), a 2px SURFACE GAP between touching segments
 * (never a stroke), a 4px rounded DATA-END with a square baseline, and a text twin
 * for every mark. Those specs want percentage widths beside pixel gaps, and CSS flex
 * gives exactly that (`flex-grow` by count, `gap` in px) where an inline `<svg>` cannot
 * mix the two units without measuring the box. The proposal's "inline SVG, no
 * dependency" was about the DEPENDENCY; this is the same no-dependency mark, drawn
 * in the layout engine that already knows its width. Recorded as a decision.
 *
 * The bar is scaled to the LARGEST total on the table (`max`), so bar LENGTH compares
 * load across developers and the segment split reads the stages within it. Zero-count
 * buckets draw nothing — a zero-width mark is not a mark — and their absence is named
 * in the text twin, which lists every bucket including the zeros.
 *
 * THE LEGEND IS SEPARATE AND RENDERED ONCE above the table (≥ 2 series → a legend
 * is always present; text wears text tokens, the swatch carries the colour). Stuck is
 * NOT an overlay on the bar: it is a column beside it and the chase list below —
 * days-in-stage is a signal about a WORK ORDER, and painting it onto a developer's
 * bar would make it read as a property of the person.
 */

import { BUCKET_LABELS, WORKLOAD_BUCKETS } from './team-api';
import type { WorkloadBucket, WorkloadBucketsDto } from './team-api';
import { BUCKET_FILL, UNASSIGNED_FILL } from './team-workload-palette';
import { cn } from '../../lib/cn';

/** The text twin — every bucket, every count, in bucket order. Screen readers get this. */
function workloadTwin(buckets: WorkloadBucketsDto): string {
  return WORKLOAD_BUCKETS.map((bucket) => `${BUCKET_LABELS[bucket]} ${String(buckets[bucket])}`).join(
    ', ',
  );
}

export function TeamWorkloadBar({
  buckets,
  max,
  label,
}: {
  buckets: WorkloadBucketsDto;
  /** The largest `total` on the table — what a full-width bar means. */
  max: number;
  /** Whose bar this is, for the accessible name. */
  label: string;
}): React.JSX.Element {
  const total = buckets.total;
  const widthPct = max <= 0 || total <= 0 ? 0 : Math.max(2, (total / max) * 100);
  const drawn = WORKLOAD_BUCKETS.filter((bucket) => buckets[bucket] > 0);

  return (
    <div
      role="img"
      aria-label={`${label}: ${total === 0 ? 'no open work' : workloadTwin(buckets)}`}
      className="flex h-3 w-full items-stretch"
    >
      {total === 0 ? (
        // An empty TRACK, not an empty bar: a hairline says "measured, nothing here".
        <span aria-hidden="true" className="h-px w-full self-center bg-line" />
      ) : (
        <span
          aria-hidden="true"
          className="flex h-full gap-0.5"
          style={{ width: `${String(widthPct)}%` }}
        >
          {drawn.map((bucket) => (
            <Segment key={bucket} bucket={bucket} count={buckets[bucket]} />
          ))}
        </span>
      )}
    </div>
  );
}

function Segment({ bucket, count }: { bucket: WorkloadBucket; count: number }): React.JSX.Element {
  return (
    <span
      data-bucket={bucket}
      title={`${BUCKET_LABELS[bucket]}: ${String(count)}`}
      className={cn(
        'block h-full min-w-0.5 first:rounded-l-[1px] last:rounded-r-[4px]',
        BUCKET_FILL[bucket],
      )}
      style={{ flexGrow: count, flexBasis: 0 }}
    />
  );
}

/** The Unassigned row's bar — one neutral mark scaled to the same `max`. */
export function UnassignedBar({ total, max }: { total: number; max: number }): React.JSX.Element {
  const widthPct = max <= 0 || total <= 0 ? 0 : Math.max(2, (total / max) * 100);
  return (
    <div
      role="img"
      aria-label={`Unassigned: ${total === 0 ? 'nothing waiting' : `${String(total)} waiting for a developer`}`}
      className="flex h-3 w-full items-stretch"
    >
      {total === 0 ? (
        <span aria-hidden="true" className="h-px w-full self-center bg-line" />
      ) : (
        <span
          aria-hidden="true"
          title={`Unassigned: ${String(total)}`}
          className={cn('block h-full rounded-r-[4px]', UNASSIGNED_FILL)}
          style={{ width: `${String(widthPct)}%` }}
        />
      )}
    </div>
  );
}

/**
 * The one legend for the whole table. Text in text tokens; the swatch carries the colour;
 * the order is the bar's order, so the legend reads left-to-right the way the bar fills.
 */
export function WorkloadLegend(): React.JSX.Element {
  return (
    <ul aria-label="Workload legend" className="flex flex-wrap items-center gap-x-4 gap-y-1">
      {WORKLOAD_BUCKETS.map((bucket) => (
        <li key={bucket} className="flex items-center gap-1.5 text-body13 text-fg-secondary">
          <span aria-hidden="true" className={cn('inline-block size-2.5 rounded-[2px]', BUCKET_FILL[bucket])} />
          {BUCKET_LABELS[bucket]}
        </li>
      ))}
      <li className="flex items-center gap-1.5 text-body13 text-fg-secondary">
        <span aria-hidden="true" className={cn('inline-block size-2.5 rounded-[2px]', UNASSIGNED_FILL)} />
        Unassigned
      </li>
    </ul>
  );
}
